import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { Tooltip, Input, Space, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { IGiveawayParticipant } from "../../../../../../interfaces";
import GiveawaysService from "../../../../../../services/http/services/GiveawaysService";
import { EmitError } from "../../../../../../utils/EmitError";
import { convertMoedaBRDoJSNovo, poeValorComPontosEmMil } from "../../../../../../utils/Conversores";
import { formatCpf, formatPhone, onlyNumbers } from "../../../../../../utils/Masks";
import { ThemeContext } from "../../../../../../contexts/ThemeContext";
import { IActiveFilterItem } from "../../../../../../components/ActiveFiltersBar";
import TableActionButton from "../../../../../../components/TableActionButton";
import { getSortOrder } from "../../../../../../hooks/SortOrder";
import { getNextSortOrder } from "../../../../../../hooks/NextSortOrder";

export interface ITableData extends IGiveawayParticipant {
    key: number | undefined;
    app_user_id: number;
    app_user_cpf: string;
    app_user_name: string;
    app_user_phone: string;
}

export interface IParticipantFilters {
    name: string | null;
    cpf: string | null;
    phone: string | null;
}

export default function useGiveawayParticipants(giveawayId: number) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const { id } = useParams();
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [rawParticipants, setRawParticipants] = useState<Array<ITableData>>([]);
    const lastSorterRef = useRef<{ field: string; order: string } | any>({ field: 'app_user_name', order: 'asc' });

    const [filters, setFilters] = useState<IParticipantFilters>({
        name: null,
        cpf: null,
        phone: null,
    });

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
    });

    const [sorter, setSorter] = useState<{ field: string; order: 'asc' | 'desc' }>({
        field: 'app_user_name',
        order: 'asc',
    });

    const [searchName, setSearchName] = useState('');
    const [searchCpf, setSearchCpf] = useState('');
    const [searchPhone, setSearchPhone] = useState('');

    const nameInputRef = useRef<any>(null);
    const phoneInputRef = useRef<any>(null);

    const handleFilterClear = useCallback((field: keyof IParticipantFilters) => {
        setFilters((prev) => ({ ...prev, [field]: null }));
        if (field === 'name') setSearchName('');
        if (field === 'cpf') setSearchCpf('');
        if (field === 'phone') setSearchPhone('');
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({ name: null, cpf: null, phone: null });
        setSearchName('');
        setSearchCpf('');
        setSearchPhone('');
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const activeFilterItems: Array<IActiveFilterItem | null> = [
        filters.name
            ? { key: 'name', label: 'Nome', value: filters.name, onClear: () => handleFilterClear('name') }
            : null,
        filters.cpf
            ? { key: 'cpf', label: 'CPF', value: formatCpf(filters.cpf), onClear: () => handleFilterClear('cpf') }
            : null,
        filters.phone
            ? { key: 'phone', label: 'Telefone', value: formatPhone(filters.phone), onClear: () => handleFilterClear('phone') }
            : null,
    ];

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setPagination({ current: page, pageSize });
    }, []);

    const handleTableChange = useCallback((
        _pag: any,
        _filts: any,
        sorterObjParam: any
    ) => {
        const sorterObj = Array.isArray(sorterObjParam) ? sorterObjParam[0] : sorterObjParam;
        const { nextSort, nextOrder } = getNextSortOrder(
            sorterObj,
            { pagination, filters, sorter },
            pagination.current,
            lastSorterRef
        );

        if (nextSort && nextOrder) {
            setSorter({ field: nextSort, order: nextOrder as 'asc' | 'desc' });
        }
    }, [pagination, filters, sorter]);

    const filteredData = useMemo(() => {
        let result = [...rawParticipants];

        if (filters.name) {
            const term = filters.name.toLowerCase();
            result = result.filter((item) => item.app_user_name?.toLowerCase().includes(term));
        }

        if (filters.cpf) {
            const rawCpf = onlyNumbers(filters.cpf);
            result = result.filter((item) => onlyNumbers(item.app_user_cpf).includes(rawCpf));
        }

        if (filters.phone) {
            const rawPhone = onlyNumbers(filters.phone);
            result = result.filter((item) => onlyNumbers(item.app_user_phone).includes(rawPhone));
        }

        if (sorter.field) {
            result.sort((a: any, b: any) => {
                const valA = a[sorter.field];
                const valB = b[sorter.field];

                if (typeof valA === 'number' && typeof valB === 'number') {
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }

                const strA = (valA || '').toString();
                const strB = (valB || '').toString();
                return sorter.order === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA);
            });
        }

        return result;
    }, [rawParticipants, filters, sorter]);

    const paginatedData = useMemo(() => {
        const start = (pagination.current - 1) * pagination.pageSize;
        return filteredData.slice(start, start + pagination.pageSize);
    }, [filteredData, pagination]);

    const columns = [
        {
            title: 'Nome / CPF',
            dataIndex: 'app_user_name',
            key: 'app_user_name',
            sorter: true,
            showSorterTooltip: false,
            sortOrder: getSortOrder('app_user_name', sorter),
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 260, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                            Nome
                        </div>
                        <Input
                            ref={nameInputRef}
                            placeholder="Buscar por nome..."
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            onPressEnter={() => {
                                confirm();
                                setFilters((prev) => ({ ...prev, name: searchName || null, cpf: searchCpf || null }));
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            allowClear
                        />
                    </div>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                            CPF
                        </div>
                        <Input
                            placeholder="000.000.000-00"
                            value={searchCpf}
                            onChange={(e) => setSearchCpf(formatCpf(e.target.value))}
                            onPressEnter={() => {
                                confirm();
                                setFilters((prev) => ({ ...prev, name: searchName || null, cpf: searchCpf || null }));
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            allowClear
                        />
                    </div>
                    <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => {
                                confirm();
                                setFilters((prev) => ({ ...prev, name: searchName || null, cpf: searchCpf || null }));
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            style={{ minWidth: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchName('');
                                setSearchCpf('');
                                if (clearFilters) clearFilters();
                                confirm();
                                setFilters((prev) => ({ ...prev, name: null, cpf: null }));
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                            style={{ minWidth: 80 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: (filters.name || filters.cpf) ? '#1677ff' : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => nameInputRef.current?.focus(), 100);
                }
            },
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontWeight: 600, color: isDark ? '#f0f0f0' : '#1e293b', fontSize: 13, lineHeight: '1.3' }}>
                        {filters.name ? (
                            <Highlighter
                                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                searchWords={[filters.name]}
                                autoEscape
                                textToHighlight={record.app_user_name || '--'}
                            />
                        ) : (
                            record.app_user_name || '--'
                        )}
                    </span>
                    <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 12, fontWeight: 500, letterSpacing: '0.3px', whiteSpace: 'nowrap', lineHeight: '1.2' }}>
                        {filters.cpf ? (
                            <Highlighter
                                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                searchWords={[filters.cpf]}
                                autoEscape
                                textToHighlight={record.app_user_cpf ? formatCpf(record.app_user_cpf) : '--'}
                            />
                        ) : (
                            record.app_user_cpf ? formatCpf(record.app_user_cpf) : '--'
                        )}
                    </span>
                </div>
            ),
            width: '28%'
        },
        {
            title: 'Telefone',
            dataIndex: 'app_user_phone',
            key: 'app_user_phone',
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 220, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569' }}>
                        Telefone
                    </div>
                    <Input
                        ref={phoneInputRef}
                        placeholder="(00) 00000-0000"
                        value={searchPhone}
                        onChange={(e) => setSearchPhone(formatPhone(e.target.value))}
                        onPressEnter={() => {
                            confirm();
                            setFilters((prev) => ({ ...prev, phone: searchPhone || null }));
                            setPagination((prev) => ({ ...prev, current: 1 }));
                        }}
                        allowClear
                    />
                    <Space style={{ justifyContent: 'space-between' }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => {
                                confirm();
                                setFilters((prev) => ({ ...prev, phone: searchPhone || null }));
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchPhone('');
                                if (clearFilters) clearFilters();
                                confirm();
                                setFilters((prev) => ({ ...prev, phone: null }));
                                setPagination((prev) => ({ ...prev, current: 1 }));
                            }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: filters.phone ? '#1677ff' : undefined }} />
            ),
            render: (phone: string) => (
                <span style={{ color: isDark ? '#a6a6a6' : '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {phone ? formatPhone(phone) : '--'}
                </span>
            ),
            width: '18%'
        },
        {
            title: 'Bilhetes',
            dataIndex: 'total_tickets',
            key: 'total_tickets',
            sorter: true,
            showSorterTooltip: false,
            sortOrder: getSortOrder('total_tickets', sorter),
            align: 'center' as const,
            render: (value?: number) => (
                <Tooltip color="#1677ff" title="Bilhetes gerados neste sorteio">
                    <span style={{ fontWeight: 700, color: '#1677ff', fontSize: 14 }}>
                        {poeValorComPontosEmMil(value) ?? '0'}
                    </span>
                </Tooltip>
            ),
            width: '15%'
        },
        {
            title: 'Saldo',
            dataIndex: 'balance_tickets',
            key: 'balance_tickets',
            sorter: true,
            showSorterTooltip: false,
            sortOrder: getSortOrder('balance_tickets', sorter),
            align: 'right' as const,
            render: (value?: number) => (
                <Tooltip color="#389e0d" title="Saldo de bilhetes">
                    <span style={{ fontWeight: 600, color: isDark ? '#52c41a' : '#237804', fontSize: 14, whiteSpace: 'nowrap' }}>
                        R$ {convertMoedaBRDoJSNovo(value) ?? '0'}
                    </span>
                </Tooltip>
            ),
            width: '18%'
        },
        {
            title: 'Ações',
            key: 'actions',
            width: '8%',
            align: 'center' as const,
            render: (_: any, record: ITableData) => (
                <TableActionButton
                    action="view"
                    tooltip="Ver cadastro do participante"
                    onClick={() => navigate(`/usuarios/aplicativo/visualizar/${record.app_user_id}`)}
                />
            )
        },
    ];

    const setTableDataFromRequest = useCallback((data: IGiveawayParticipant[]) => {
        if (!data) return;
        const newData = data.map((participant) => ({
            ...participant,
            key: participant.id,
            app_user_id: participant?.id!,
            app_user_name: participant?.name!,
            app_user_cpf: participant?.cpf!,
            app_user_phone: participant?.phone!,
        }));
        setRawParticipants(newData);
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function loadParticipants() {
            try {
                setIsLoading(true);
                const giveawayParticipantsData = await GiveawaysService.listGiveawayParticipantsById({
                    id: Number(id || giveawayId),
                    signal: controller.signal
                });

                setTableDataFromRequest(giveawayParticipantsData);
                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }
                EmitError(err, true, 'giveawayParticipants_aosdpasdpasd');
                setIsLoading(false);
            }
        }

        loadParticipants();

        return () => controller.abort();
    }, [setTableDataFromRequest, id, giveawayId]);

    return {
        columns,
        isLoading,
        tableData: paginatedData,
        totalParticipants: filteredData.length,
        pagination,
        activeFilterItems,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        navigate,
    };
}
